import React, { useState } from "react";
import { Check, Zap, Eye, Sparkles, ChevronDown, ChevronUp, Shield, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// ✅ Configuração de URLs e Preços
const CHECKOUT_UPSELL_20 = "https://pay.kiwify.com.br/3hUHSAF"; 

const benefits = [
  {
    icon: Zap,
    title: "Edição 10x mais rápida",
    desc: "Otimize seu processo arrastando e soltando cenas prontas na sua timeline.",
  },
  {
    icon: Eye,
    title: "Retenção Absurda",
    desc: "Recursos que aumentam o tempo de tela e deixam o vídeo vivo do início ao fim.",
  },
  {
    icon: Sparkles,
    title: "Acabamento de Elite",
    desc: "Ritmo e impacto cinematográfico sem precisar inventar nada do zero.",
  },
];

const stack = [
  { title: "Legenda Pro 2.0 (PR + AE)", desc: "O estilo viral que domina o algoritmo." },
  { title: "Scripts de Automação", desc: "Importação e empilhamento automático no After Effects." },
  { title: "LUTs & SFX Premium", desc: "Cor e impacto sonoro profissional em 1 clique." },
  { title: "Biblioteca de Hooks", desc: "Estruturas prontas para prender a atenção em qualquer nicho." },
];

const faqs = [
  {
    q: "Se eu recusar, eu perco o meu Pack?",
    a: "Não. O seu acesso ao produto principal está garantido. Você apenas perde este desconto único do Combo Pro.",
  },
  {
    q: "Por que o valor está tão baixo?",
    a: "No site o Combo Pro custa R$ 49. Liberamos por +R$ 20 apenas nesta página como um presente de boas-vindas.",
  },
  {
    q: "O acesso é imediato?",
    a: "Sim. Assim que completar o upgrade, todos os recursos aparecem na sua área de membros.",
  },
];

const UpsellPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const toggleFaq = (index) => setOpenFaq(openFaq === index ? null : index);

  const handleAccept = () => { window.location.href = CHECKOUT_UPSELL_20; };
  const handleDecline = () => { window.location.href = "/obrigado"; };

  return (
    <div className="min-h-screen bg-black text-white font-sans antialiased">
      <div className="relative pb-32 md:pb-12 overflow-hidden">
        
        {/* HEADER / ANCORAGEM */}
        <section className="relative px-4 pt-12 text-center max-w-4xl mx-auto">
          <Badge variant="outline" className="mb-6 border-orange-500/50 bg-orange-500/10 text-orange-500 px-4 py-1.5 animate-pulse">
            <Clock className="w-3 h-3 mr-2" /> OFERTA EXCLUSIVA: ECONOMIZE R$ 29,00 AGORA
          </Badge>

          <h1 className="text-3xl sm:text-4xl md:text-6xl font-black leading-tight mb-6">
            Você vai liberar o <span className="text-orange-500 text-gradient-orange">Combo Pro</span> por apenas <span className="text-orange-500">+R$20</span>?
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-gray-400 mb-8 leading-relaxed max-w-2xl mx-auto">
            Parabéns! O Pack de Cenas já é seu. <br className="hidden sm:block"/>
            Mas ter o Arsenal Completo para editar mais rápido é o que te diferencia dos amadores.
          </p>

          {/* ÁREA DO BOTÃO CORRIGIDA */}
          <div className="flex flex-col gap-6 justify-center items-center mb-12 px-2">
            <Button 
              onClick={handleAccept} 
              className="w-full max-w-md h-auto py-5 px-4 text-sm sm:text-base md:text-lg font-black uppercase tracking-tight bg-orange-600 hover:bg-orange-500 text-white rounded-xl shadow-[0_0_30px_rgba(234,88,12,0.4)] transition-all hover:scale-105 whitespace-normal break-words"
            >
              SIM! ADICIONAR AO MEU ACESSO (+R$20)
            </Button>
            
            <button 
              onClick={handleDecline} 
              className="text-gray-500 hover:text-white text-xs sm:text-sm underline underline-offset-4 transition-colors"
            >
              Não, quero seguir apenas com o Pack simples
            </button>
          </div>
        </section>

        {/* BENEFÍCIOS */}
        <section className="px-4 py-12 bg-zinc-950">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <div key={i} className="p-6 rounded-2xl border border-zinc-800 bg-zinc-900/50">
                <b.icon className="w-10 h-10 text-orange-500 mb-4" />
                <h3 className="font-bold text-xl mb-2 text-white">{b.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* LISTA DE ENTREGA */}
        <section className="px-4 py-16 max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-black mb-10 text-white">O que você recebe no Combo Pro:</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
            {stack.map((item, i) => (
              <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-zinc-900 border border-zinc-800 text-left">
                <div className="bg-orange-500/20 p-2 rounded-lg flex-shrink-0"><Check className="text-orange-500 w-5 h-5" /></div>
                <div>
                  <h4 className="font-bold text-sm text-white">{item.title}</h4>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="inline-block p-6 rounded-2xl bg-orange-500/5 border border-orange-500/20 mb-8 max-w-lg">
            <p className="text-gray-300 italic text-sm md:text-base">"Pensa: +R$ 20 é menos que um lanche. Mas pode economizar horas em cada edição."</p>
          </div>
        </section>

        {/* FAQ E GARANTIA */}
        <section className="px-4 pb-20 max-w-2xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-10 text-green-500">
            <Shield className="w-5 h-5" /> <span className="font-bold uppercase tracking-wider text-xs sm:text-sm">Garantia Incondicional de 7 Dias</span>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-900/30">
                <button 
                  onClick={() => toggleFaq(i)} 
                  className="w-full flex items-center justify-between p-5 text-left font-bold text-sm md:text-base text-white"
                >
                  <span>{faq.q}</span> {openFaq === i ? <ChevronUp className="flex-shrink-0" /> : <ChevronDown className="flex-shrink-0" />}
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-gray-400 text-sm leading-relaxed">{faq.a}</div>}
              </div>
            ))}
          </div>
        </section>

      </div>

      {/* MOBILE STICKY BAR */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-black/90 backdrop-blur-md border-t border-zinc-800 md:hidden z-50">
        <Button onClick={handleAccept} className="w-full h-14 font-black bg-orange-600 text-white text-base rounded-xl">
          LIBERAR COMBO PRO (+R$20)
        </Button>
      </div>
    </div>
  );
};

export default UpsellPage;
